"use client";

import { useState, useEffect, useCallback } from "react";
import Image, { StaticImageData } from "next/image";

interface Photo {
  src: StaticImageData;
  alt: string;
  label: string;
}

export default function PhotoGallery({ photos }: { photos: Photo[] }) {
  const [open, setOpen] = useState<number | null>(null);

  const close = useCallback(() => setOpen(null), []);

  const prev = useCallback(() => {
    setOpen(i => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  const next = useCallback(() => {
    setOpen(i => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (open === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape")     close();
      if (e.key === "ArrowLeft")  prev();
      if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {photos.map((photo, i) => (
          <div key={photo.alt} className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">{photo.label}</p>
            <button
              onClick={() => setOpen(i)}
              className="relative rounded-xl overflow-hidden h-52 sm:h-64 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400"
              aria-label={`Enlarge: ${photo.alt}`}
            >
              <Image src={photo.src} alt={photo.alt} fill className="object-cover transition-transform duration-300 hover:scale-105" placeholder="blur" />
            </button>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {open !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={close}
        >
          {/* Prev */}
          <button
            onClick={e => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl p-3 hover:text-yellow-400 focus:outline-none"
            aria-label="Previous photo"
          >
            ‹
          </button>

          {/* Image */}
          <div
            className="relative max-w-5xl w-full mx-16 max-h-[85vh] aspect-[4/3]"
            onClick={e => e.stopPropagation()}
          >
            <Image
              src={photos[open].src}
              alt={photos[open].alt}
              fill
              className="object-contain"
              placeholder="blur"
              sizes="(max-width: 1280px) 100vw, 1280px"
            />
          </div>

          {/* Next */}
          <button
            onClick={e => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl p-3 hover:text-yellow-400 focus:outline-none"
            aria-label="Next photo"
          >
            ›
          </button>

          {/* Caption + close */}
          <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
              {photos[open].label}
            </p>
            <button onClick={close} className="text-gray-500 hover:text-white text-xs underline">
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
